import React, { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import DialogFooter from './DialogFooter';
import Pagination from './Pagination';
import NoProductImage from './NoProductImage';
import ProductPreviewCard from '../ProductPreviewCard';
import { useProductSelection } from '../hooks/useProductSelection';

export type ProductSelectionApi = {
  getProductTotalsAbortable?: (params: any) => { promise: Promise<any>; cancel: () => void };
  getMarkets?: (organizationId: number) => Promise<any[]>;
  [key: string]: any;
};

type ProductMatch = {
  id?: number | string;
  productId?: number | string;
  name?: string;
  productName?: string;
  brandName?: string;
  brand?: string;
  productCategoryName?: string;
  category?: string;
  strain?: string;
  description?: string;
  productImageUrl?: string;
  imageUrl?: string;
  inventoryUpc?: string;
  marketIds?: number[];
  markets?: { id: number; name: string }[];
};

type ProductSelectionModalProps = {
  show: boolean;
  api?: ProductSelectionApi;
  organizationId: number;
  onSelect: (product: ProductMatch) => void;
  onCancel: () => void;
  initialMarketId?: number | string | null;
};

const PAGE_SIZE = 12;

export default function ProductSelectionModal({
  show,
  api,
  organizationId,
  onSelect,
  onCancel,
  initialMarketId = null,
}: ProductSelectionModalProps) {
  const {
    markets, marketId, query, results, total, page, loading, selectedProduct,
    setSelectedProduct, changeQuery, changeMarket, changePage, invalidateProducts,
  } = useProductSelection({ show, api, organizationId, initialMarketId });
  const handleCancel = useCallback(() => {
    invalidateProducts();
    onCancel();
  }, [invalidateProducts, onCancel]);
  const handleSelect = useCallback((product: ProductMatch) => {
    invalidateProducts();
    onSelect(product);
  }, [invalidateProducts, onSelect]);

  //escape key handler
  useEffect(() => {
    if (!show) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [show, handleCancel]);

  if (!show) return null;
  if (typeof document === 'undefined') return null;

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const getProductKey = (item: ProductMatch | null | undefined) => {
    if (!item) return undefined;
    const raw = item.id ?? item.productId;
    return raw == null ? undefined : String(raw);
  };
  const getLabel = (item: ProductMatch) => String(item?.name || item?.productName || 'Untitled product');
  const getImageUrl = (item: ProductMatch) => item?.productImageUrl || item?.imageUrl || '';
  const getBrand = (item: ProductMatch) => item?.brandName || item?.brand || '';
  const getCategory = (item: ProductMatch) => item?.productCategoryName || item?.category || '';
  const getMarketTags = (item: ProductMatch) => {
    if (Array.isArray(item?.markets) && item.markets.length) return item.markets;
    if (Array.isArray(item?.marketIds) && item.marketIds.length && markets.length) {
      return markets.filter((m) => item.marketIds!.includes(m.id));
    }
    return [];
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Select Product"
      data-testid="product-selection-modal"
      onClick={handleCancel}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        zIndex: 20000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        className="drawer-controls-card"
        onClick={(e) => e.stopPropagation()}
        data-testid="product-selection-modal-content"
        style={{
          width: 960,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'calc(100vh - 64px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="modal-title" style={{ margin: 0 }}>Select Product</div>
            <button
              type="button"
              onClick={handleCancel}
              style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', padding: 4 }}
              aria-label="Close"
              data-testid="product-selection-modal-close"
            >
              &times;
            </button>
          </div>
        </div>

        {/* Filters */}
        <div style={{ padding: '12px 20px', display: 'flex', gap: 12, flexWrap: 'wrap', borderBottom: '1px solid var(--border)' }}>
          <div style={{ flex: '0 0 200px' }}>
            <label className="label" style={{ fontSize: 12 }}>Market</label>
            <select
              className="input"
              value={marketId}
              onChange={(e) => {
                changeMarket(e.target.value);
              }}
              style={{ width: '100%' }}
              data-testid="product-selection-modal-market"
            >
              <option value="">All Markets</option>
              {markets.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <label className="label" style={{ fontSize: 12 }}>Search</label>
            <input
              className="input"
              type="text"
              placeholder="Search products by name"
              value={query}
              onChange={(e) => {
                changeQuery(e.target.value);
              }}
              style={{ width: '100%' }}
              autoFocus
              autoComplete="off"
              data-testid="product-selection-modal-search"
            />
          </div>
        </div>

        {/* body */}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', padding: '12px 20px', gap: 16, minHeight: 0 }}>
          {/* product tile grid */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
            <div
              data-testid="product-selection-modal-results"
              style={{
                flex: 1,
                overflowY: 'auto',
                minHeight: 0,
              }}
            >
              {loading && (
                <div className="muted" style={{ padding: 12, fontSize: 13 }} data-testid="product-selection-modal-loading">Loading products...</div>
              )}
              {!loading && results.length === 0 && (
                <div className="muted" style={{ padding: 12, fontSize: 13 }} data-testid="product-selection-modal-empty">No products found.</div>
              )}
              {!loading && results.length > 0 && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 12,
                  }}
                >
                  {results.map((item, index) => {
                    const key = getProductKey(item);
                    const selectedKey = getProductKey(selectedProduct);
                    const itemKey = getProductKey(item);
                    const isSelected = selectedKey !== undefined && selectedKey === itemKey;
                    const imageUrl = getImageUrl(item);
                    const productMarkets = getMarketTags(item);

                    return (
                      <div
                        key={key ?? `missing-product-${index}`}
                        onClick={() => setSelectedProduct(item)}
                        onDoubleClick={() => handleSelect(item)}
                        style={{
                          border: isSelected ? '2px solid var(--primary)' : '2px solid var(--border)',
                          borderRadius: 10,
                          cursor: 'pointer',
                          overflow: 'hidden',
                          background: 'var(--card-bg, #fff)',
                          transition: 'border-color 0.15s',
                        }}
                        data-testid={key ? `product-option-${key}` : undefined}
                        data-selected={isSelected}
                      >
                        {/* image */}
                        <div
                          data-testid={key ? `product-option-image-${key}` : undefined}
                          style={{
                            width: '100%',
                            height: 120,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundImage: imageUrl ? `url(${imageUrl})` : undefined,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundColor: imageUrl ? undefined : 'var(--bg-muted, #f5f5f5)',
                          }}
                        >
                          {!imageUrl && <span data-testid={key ? `product-option-image-empty-${key}` : undefined}><NoProductImage scale={1.5} /></span>}
                        </div>

                        {/* body */}
                        <div style={{ padding: '8px 10px 10px' }}>
                          {getBrand(item) && (
                            <div className="muted clamp-1" style={{ fontSize: 11 }} data-testid={key ? `product-option-brand-${key}` : undefined}>{getBrand(item)}</div>
                          )}
                          <div className="clamp-2" style={{ fontWeight: 700, fontSize: 13, lineHeight: '1.3' }} title={getLabel(item)} data-testid={key ? `product-option-name-${key}` : undefined}>
                            {getLabel(item)}
                          </div>
                          {productMarkets.length > 0 && (
                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                              {productMarkets.map((m) => (
                                <span key={m.id} className="tag" style={{ fontSize: 10 }}>{m.name}</span>
                              ))}
                            </div>
                          )}
                          {getCategory(item) && (
                            <div style={{ marginTop: 4 }} data-testid={key ? `product-option-category-${key}` : undefined}>
                              <span className="tag subtle" style={{ fontSize: 10 }}>{getCategory(item)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* pagination */}
            {totalPages > 1 && (
              <div style={{ marginTop: 8, flexShrink: 0 }}>
                <Pagination
                  testId="product-selection-modal-pagination"
                  page={page}
                  totalPages={totalPages}
                  hideWhenSingle
                  prevDisabled={loading || page === 0}
                  nextDisabled={loading || page >= totalPages - 1}
                  onPrev={() => {
                    changePage(Math.max(0, page - 1));
                  }}
                  onNext={() => {
                    changePage(page + 1);
                  }}
                  prevLabel="◀"
                  nextLabel="▶"
                  format="of"
                />
              </div>
            )}
          </div>

          {/* preview panel */}
          <div
            data-testid="product-selection-modal-preview"
            style={{
              flex: '0 0 240px',
              overflowY: 'auto',
              minHeight: 0,
            }}
          >
            {!selectedProduct ? (
              <div className="card" style={{ padding: 16 }} data-testid="product-selection-modal-preview-empty">
                <div className="muted" style={{ fontSize: 12 }}>Click a product to see its preview.</div>
              </div>
            ) : (
              <ProductPreviewCard
                id={selectedProduct.id}
                name={getLabel(selectedProduct)}
                brand={getBrand(selectedProduct)}
                category={getCategory(selectedProduct)}
                strain={selectedProduct.strain || ''}
                inventoryUpc={selectedProduct.inventoryUpc || ''}
                imageName=""
                imageUrl={getImageUrl(selectedProduct)}
                markets={getMarketTags(selectedProduct).map((m) => m.name || String(m.id))}
                description={selectedProduct.description || ''}
                container="card"
                testId="product-selection-modal-preview-card"
              />
            )}
          </div>
        </div>

        {/* footer */}
        <div style={{ padding: '12px 20px 16px', borderTop: '1px solid var(--border)' }}>
          <DialogFooter>
            <button className="button secondary" onClick={handleCancel} data-testid="product-selection-modal-cancel">
              Cancel
            </button>
            <button
              className="button"
              disabled={!selectedProduct}
              onClick={() => { if (selectedProduct) handleSelect(selectedProduct); }}
              data-testid="product-selection-modal-confirm"
            >
              Select Product
            </button>
          </DialogFooter>
        </div>
      </div>
    </div>,
    document.body,
  );
}
